import { API_URLS, MAX_BET_REACHED_POPUP } from '@/utils/constant';
import { assertBetAccepted, getData, mathFloor, postData } from '@/utils/helpers';
import { IBetData, SelectionTypes } from '@/utils/index.model';
import { cloneDeep, reduce, find } from 'lodash';

interface IBetState {
  bet: {
    activeChip: number;
    betData: IBetData;
    myBets: IBetData;
    betHistory: IBetData[];
  };
  videoToast: IBetVideoToast | null;
}

// The pill over the video (components/tai-xiu/video-toast.vue), as on Tài Xỉu.
export interface IBetVideoToast {
  key: number;
  kind: 'placed' | 'cancelled';
  amount: number;
}

// Seeded from the clock: `bets` is persisted, and a key that restarted at 0
// after a refresh could repeat the persisted one and be taken as no change.
let videoToastKey = Date.now();

const BET_DATA = {
  activeChip: 10,
  betData: {
    banker: 0,
    draw: 0,
    player: 0,
  },
  myBets: {
    banker: 0,
    draw: 0,
    player: 0,
  },
  betHistory: [],
};

export const state = () => ({ bet: { ...BET_DATA }, videoToast: null });

export const mutations = {
  UPDATE_ACTIVE_CHIP(stateData: IBetState, activeChip: number): void {
    stateData.bet.activeChip = activeChip;
  },
  UPDATE_BET(stateData: IBetState, betData: IBetData): void {
    stateData.bet.betData = { ...betData };
  },
  UPDATE_MY_BETS(stateData: IBetState, betData: IBetData): void {
    stateData.bet.myBets = { ...betData };
  },
  UPDATE_BET_HISTORY(stateData: IBetState, betData: IBetData): void {
    stateData.bet.betHistory.push(betData);
  },
  UNDO_BET(stateData: IBetState): void {
    stateData.bet.betHistory.pop();
    const betData = (stateData.bet.betHistory[stateData.bet.betHistory.length - 1] as IBetData) || {
      banker: 0,
      draw: 0,
      player: 0,
    };
    stateData.bet.betData = { ...betData };
  },
  RESET_BET_HISTORY(stateData: IBetState): void {
    stateData.bet.betHistory = [];
  },
  RESET_BET_DATA(stateData: IBetState): void {
    stateData.bet.betData = {
      banker: 0,
      draw: 0,
      player: 0,
    };
  },
  SHOW_VIDEO_TOAST(stateData: IBetState, payload: { kind: IBetVideoToast['kind']; amount?: number }): void {
    videoToastKey += 1;
    stateData.videoToast = { key: videoToastKey, kind: payload.kind, amount: payload.amount || 0 };
  },
  RESET_MY_BETS(stateData: IBetState): void {
    stateData.bet.myBets = {
      banker: 0,
      draw: 0,
      player: 0,
    };
  },
};

export const actions = {
  updateActiveChip(context: any, activeChip: number): void {
    context.commit('UPDATE_ACTIVE_CHIP', activeChip);
  },
  placeBet(context: any, type: SelectionTypes): void {
    const { commit, state: stateData, rootGetters } = context;
    const { activeChip, betData, myBets } = stateData.bet;
    const balance = rootGetters['general/realBalance'];
    const {
      mainLimitAmountPerFight: configMainLimit,
      drawLimitAmountPerFight: configDrawLimit,
      betChips,
    } = rootGetters['general/config'];
    const { mainLimitAmountPerFight: arenaMainLimit, drawLimitAmountPerFight: arenaDrawLimit } =
      rootGetters['general/selectedArena'];
    const activeChipVal = Number(activeChip) < Number(betChips[0]) ? Number(betChips[0]) : Number(activeChip);
    const betDataTemp = cloneDeep(betData);
    const baseAmount = betDataTemp[type];

    const chipUpdateValue = baseAmount + activeChipVal >= 0 ? activeChipVal : 0;
    // Here add ante for value will be fine
    const remainBalance = reduce(
      betDataTemp,
      (curBalance, value, key) => {
        if (key !== type) {
          return curBalance - value;
        }
        return curBalance;
      },
      balance
    );

    // Don't allow user place bet less than first chip
    if ((baseAmount === 0 && remainBalance < Number(betChips[0])) || balance < Number(betChips[0])) return;

    const mainLimit = arenaMainLimit ? Math.min(configMainLimit, arenaMainLimit) : configMainLimit;
    const drawLimit = arenaDrawLimit ? Math.min(configDrawLimit, arenaDrawLimit) : configDrawLimit;

    const limitAmountPerFight =
      type === SelectionTypes.draw ? drawLimit - myBets.draw : mainLimit - myBets.player - myBets.banker;
    const newBetAmount = Math.min(mathFloor(remainBalance), limitAmountPerFight, baseAmount + chipUpdateValue);

    if (!newBetAmount) context.dispatch('menu/updateMenu', MAX_BET_REACHED_POPUP, { root: true });
    betDataTemp[type] = newBetAmount;

    commit('UPDATE_BET', betDataTemp);
    if (betData[type] !== betDataTemp[type]) {
      commit('UPDATE_BET_HISTORY', betDataTemp);
    }
  },
  resetBetHistory(context: any): void {
    context.commit('RESET_BET_HISTORY');
  },
  resetBetData(context: any): void {
    context.commit('RESET_BET_DATA');
  },
  undoBet(context: any): void {
    context.commit('UNDO_BET');
  },
  /*
   * Betting closed: drop the chips that were never sent, and if there were
   * any, say so on the video ("Unconfirmed bets cancelled"), as Tài Xỉu does.
   */
  dropUnconfirmedBets(context: any): void {
    const { banker, player, draw } = context.state.bet.betData;
    const hadDrafts = banker + player + draw > 0;
    context.commit('RESET_BET_DATA');
    context.commit('RESET_BET_HISTORY');
    if (hadDrafts) context.commit('SHOW_VIDEO_TOAST', { kind: 'cancelled' });
  },
  submitBet(context: any): Promise<void> {
    const { state: stateData, rootGetters } = context;
    const { betData } = stateData.bet;
    const oddsType = rootGetters['general/oddsType'];
    const { id } = rootGetters['fight/fight'];
    const token = rootGetters['general/token'];
    if (betData.banker + betData.player + betData.draw === 0) return new Promise((resolve) => resolve());
    /*
     * The odds on the bet zone at the moment of placing (bet-zone/info.vue,
     * which shows draw as odds + 1). The ticket only carries odds once it
     * settles, so the rail shows these until then. A pane's actions are
     * scoped (store/cockfight-pane.ts), so this is that pane's own fight.
     */
    const selections = rootGetters['fight/fight'].selections || [];
    const oddsOf = (name: string) => Number((find(selections, { name }) || {}).odds || 0);
    const placedOdds: { [selection: string]: number } = {};
    if (betData.banker > 0 && oddsOf('banker')) placedOdds.banker = oddsOf('banker');
    if (betData.player > 0 && oddsOf('player')) placedOdds.player = oddsOf('player');
    if (betData.draw > 0 && oddsOf('draw')) placedOdds.draw = oddsOf('draw') + 1;
    // API call
    const params = [];
    if (betData.banker > 0)
      params.push({
        fight_id: id,
        selection: 'banker',
        amount: betData.banker,
        odds_type: oddsType,
      });
    if (betData.player > 0)
      params.push({
        fight_id: id,
        selection: 'player',
        amount: betData.player,
        odds_type: oddsType,
      });
    if (betData.draw > 0)
      params.push({
        fight_id: id,
        selection: 'draw',
        amount: betData.draw,
        odds_type: 'eu',
      });
    return postData(API_URLS.BET, token, params).then((res) => {
      assertBetAccepted(res);
      context.commit('SHOW_VIDEO_TOAST', { kind: 'placed', amount: betData.banker + betData.player + betData.draw });
      context.commit('RESET_BET_DATA');
      context.commit('RESET_BET_HISTORY');
      context.dispatch('ticket/rememberPlacedOdds', { fightId: id, odds: placedOdds }, { root: true });
      // Show the new bet in the desktop rail's CURRENT list straight away.
      context.dispatch('ticket/refreshAfterBet', null, { root: true });
      return res;
    });
  },
  getMyBets(context: any, fightId: string): Promise<void> {
    const { rootGetters } = context;
    const token = rootGetters['general/token'];

    return getData(`${API_URLS.FIGHT_HISTORY}${fightId}/${API_URLS.MY_BET}`, token).then((res) => {
      const bankerAmount = (find(res, { selection: 'banker' }) || {}).total_amount || 0;
      const drawAmount = (find(res, { selection: 'draw' }) || {}).total_amount || 0;
      const playerAmount = (find(res, { selection: 'player' }) || {}).total_amount || 0;

      const myBets = {
        banker: bankerAmount,
        draw: drawAmount,
        player: playerAmount,
      };
      context.commit('UPDATE_MY_BETS', myBets);
    });
  },
  resetMyBets(context: any): void {
    context.commit('RESET_MY_BETS');
  },
};

export const getters = {
  activeChip: (stateData: any): number => {
    return stateData.bet.activeChip;
  },
  betData: (stateData: any): IBetData => {
    return stateData.bet.betData;
  },
  myBets: (stateData: any): IBetData => {
    return stateData.bet.myBets;
  },
  videoToast: (stateData: any): IBetVideoToast | null => {
    return stateData.videoToast || null;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
